import { API_BASE } from '../apiBase';
// src/employees/EmployeeProfile.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAddressSuggestions, geocodePlaceId } from '../lib/googlePlaces';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { format, parseISO } from 'date-fns';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

function EmployeeProfile() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [copiedCell, setCopiedCell] = useState(null);
  const [timeRange, setTimeRange] = useState('all');

  // Edit modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    phone: '',
    email: '',
    homeAddress: { street: '', city: '', state: 'CA', zip: '' },
    startDate: '',
    payRate: '',
    newRaises: [],
  });

  // ---- Home-address Google Places search (manual entry as fallback) ----
  const [addrQuery, setAddrQuery] = useState('');
  const [addrSuggestions, setAddrSuggestions] = useState([]);
  const [addrError, setAddrError] = useState(null);
  const [manualAddr, setManualAddr] = useState(false);
  const addrTimer = React.useRef(null);
  const fetchAddrSuggestions = (q) => {
    clearTimeout(addrTimer.current);
    if (!q || q.length < 3) { setAddrSuggestions([]); setAddrError(null); return; }
    addrTimer.current = setTimeout(async () => {
      const { predictions, error } = await getAddressSuggestions(q);
      setAddrError(error);
      setAddrSuggestions(predictions || []);
    }, 400);
  };
  useEffect(() => {
    const fetchEmployee = async () => {
      const token = localStorage.getItem('token');
      if (!token) return navigate('/login');

      try {
        setLoading(true);
        const res = await axios.get(`${API_BASE}/employees/${id}`, {
          headers: { Authorization: token },
        });
        setEmployee(res.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    };
    fetchEmployee();
  }, [id, navigate]);

  useEffect(() => {
    if (isEditModalOpen && employee) {
      setFormData({
        phone: employee.phone || '',
        email: employee.email || '',
        homeAddress: employee.homeAddress || { street: '', city: '', state: 'CA', zip: '' },
        startDate: employee.startDate ? new Date(employee.startDate).toISOString().split('T')[0] : '',
        payRate: employee.payRate || '',
        newRaises: [],
      });
      // The search bar itself displays the saved address — but only a REAL
      // one (a bare default "CA" state isn't an address).
      const ha = employee.homeAddress || {};
      setAddrQuery(ha.street ? [ha.street, ha.city, ha.state, ha.zip].filter(Boolean).join(', ') : '');
      setAddrSuggestions([]);
      setManualAddr(false);
    }
  }, [isEditModalOpen, employee]);

  // Phone formatting
  const formatPhone = (value) => {
    if (!value) return '';
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (raw.length > 10) return;
    setFormData((prev) => ({ ...prev, phone: raw }));
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCell({ field });
      setTimeout(() => setCopiedCell(null), 3000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('homeAddress.')) {
      const key = name.split('.')[1];
      setFormData((prev) => ({
        ...prev,
        homeAddress: { ...prev.homeAddress, [key]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const addRaiseRow = () => {
    setFormData((prev) => ({
      ...prev,
      newRaises: [...prev.newRaises, { date: '', payRate: '', raiseAmount: '', notes: '' }],
    }));
  };

  const updateNewRaise = (index, field, value) => {
    setFormData((prev) => {
      const newRaises = [...prev.newRaises];
      newRaises[index] = { ...newRaises[index], [field]: value };
      return { ...prev, newRaises };
    });
  };

  const removeNewRaise = (index) => {
    setFormData((prev) => ({
      ...prev,
      newRaises: prev.newRaises.filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const payload = {
        phone: formData.phone || undefined,
        email: formData.email || undefined,
        homeAddress: formData.homeAddress,
        startDate: formData.startDate || undefined,
        payRate: formData.payRate ? Number(formData.payRate) : undefined,
        newRaises: formData.newRaises
          .filter((r) => r.date && r.payRate)
          .map((r) => ({
            date: r.date,
            payRate: Number(r.payRate),
            raiseAmount: r.raiseAmount ? Number(r.raiseAmount) : 0,
            notes: r.notes || '',
          })),
      };

      const res = await axios.put(`${API_BASE}/employees/${id}`, payload, {
        headers: { Authorization: token },
      });

      setEmployee(res.data);
      setIsEditModalOpen(false);
    } catch (err) {
      alert('Failed to update: ' + (err.response?.data?.message || err.message));
    }
  };

  const getFilteredHistory = () => {
    if (!employee?.payRaiseHistory?.length) return [];
    const now = new Date();
    let cutoff;

    if (timeRange === '12m') cutoff = new Date(now.setMonth(now.getMonth() - 12));
    else if (timeRange === '6m') cutoff = new Date(now.setMonth(now.getMonth() - 6));
    else if (timeRange === '30d') cutoff = new Date(now.setDate(now.getDate() - 30));
    else return employee.payRaiseHistory;

    return employee.payRaiseHistory.filter(r => new Date(r.date) >= cutoff);
  };

  const safeParseDate = (val) => {
    if (!val) return null;
    const d = typeof val === 'string' ? parseISO(val) : new Date(val);
    return isNaN(d) ? null : d;
  };

  const startDate = safeParseDate(employee?.startDate);
  const lastRaiseDate = safeParseDate(employee?.lastRaiseDate);

  const filteredHistory = getFilteredHistory();

  const labels = [];
  const points = [];

  if (filteredHistory.length) {
    filteredHistory.forEach(e => {
      labels.push(format(new Date(e.date), 'MMM d, yyyy'));
      points.push(e.payRate);
    });
  } else if (employee?.payRate) {
    labels.push(timeRange === 'all' ? 'Current' : 'No raises in period');
    points.push(employee.payRate);
  }

  const chartData = {
    labels,
    datasets: [{
      label: 'Hourly Pay Rate',
      data: points,
      borderColor: '#a3e635',
      backgroundColor: '#a3e635',
      pointBackgroundColor: '#a3e635',
      pointBorderColor: '#000',
      pointBorderWidth: 2,
      pointRadius: 5,
      pointHoverRadius: 8,
      tension: 0.3,
      borderWidth: 3,
    }],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(30,30,46,0.95)',
        titleColor: '#a3e635',
        bodyColor: '#e5e7eb',
        borderColor: '#4b5563',
        borderWidth: 1,
        padding: 12,
        callbacks: {
          title: t => t[0].label,
          label: c => {
            const v = c.parsed.y;
            let t = '';
            if (c.dataIndex > 0) {
              const r = filteredHistory[c.dataIndex - 1];
              if (r?.raiseAmount) t = ` (+$${Number(r.raiseAmount).toFixed(2)})`;
            }
            return `$${v.toFixed(2)}/hr${t}`;
          },
        },
      },
    },
    scales: {
      x: { grid: { color: 'rgba(75,85,99,0.2)' }, ticks: { color: '#9ca3af' } },
      y: {
        grid: { color: 'rgba(75,85,99,0.2)' },
        ticks: { color: '#9ca3af', callback: v => `$${v}` },
        beginAtZero: false,
        suggestedMin: (Math.min(...points) || 20) * 0.9,
      },
    },
  };

  if (loading) return <div className="min-h-screen bg-gray-950 p-8 text-zinc-100">Loading...</div>;
  if (error) return <div className="min-h-screen bg-gray-950 p-8 text-red-400">{error}</div>;
  if (!employee) return <div className="min-h-screen bg-gray-950 p-8 text-zinc-100">Employee not found</div>;

  return (
    <div className="min-h-screen bg-gray-950 text-zinc-100 p-8 max-md:p-3 space-y-10 max-md:space-y-5 max-md:overflow-x-hidden">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 border-b border-gray-800 pb-8">
        <div className="flex items-center gap-6 max-md:gap-3 min-w-0">
          <img
            src={`https://via.placeholder.com/120?text=${employee.name?.slice(0,2)||'EMP'}`}
            alt={employee.name}
            className="w-24 h-24 max-md:w-14 max-md:h-14 rounded-full object-cover border-4 border-indigo-600/30 shrink-0"
          />
          <div>
            <h1 className="text-3xl max-md:text-xl font-bold tracking-tight break-words">
              <span
                className="cursor-pointer relative group inline-block"
                onClick={() => copyToClipboard(employee.name||'N/A','name')}
              >
                {employee.name||'N/A'}
                {copiedCell?.field==='name'&&(
                  <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-800 text-zinc-200 text-xs px-3 py-1 rounded-md shadow-lg z-10">
                    Copied!
                  </span>
                )}
              </span>
            </h1>
            <p className="text-zinc-400 mt-1">
              {employee.role||'Not set'} • {employee.status||'Active'}
            </p>
          </div>
        </div>
        <div className="flex gap-4">
          <button
            onClick={() => navigate('/employees')}
            className="px-6 py-3 max-md:px-4 max-md:py-2 max-md:text-sm bg-gray-700 hover:bg-gray-600 rounded-xl font-medium transition-all"
          >
            Back to List
          </button>
          <button
            onClick={() => setIsEditModalOpen(true)}
            className="px-6 py-3 max-md:px-4 max-md:py-2 max-md:text-sm bg-indigo-700 hover:bg-indigo-600 text-white rounded-xl font-medium shadow-md transition-all hover:shadow-lg"
          >
            Edit Profile
          </button>
        </div>
      </div>

      <div className="border-b border-gray-800">
        <nav className="flex space-x-8 overflow-x-auto pb-4">
          {['overview','projects','documents','notes'].map(tab=>(
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-4 px-2 text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === tab ? 'text-indigo-400 border-b-2 border-indigo-500' : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              {tab.charAt(0).toUpperCase()+tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      <div className="space-y-10">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              <div className="bg-gray-900 rounded-2xl border border-gray-800/50 p-8 max-md:p-4 shadow-2xl">
                <h2 className="text-xl font-bold mb-6">Contact Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-zinc-400 text-sm">Phone</p>
                    <p
                      className="text-lg font-medium mt-1 cursor-pointer"
                      onClick={() => copyToClipboard(employee.phone||'','phone')}
                    >
                      {formatPhone(employee.phone)||'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">Email</p>
                    <p
                      className="text-lg font-medium mt-1 cursor-pointer"
                      onClick={() => copyToClipboard(employee.email||'','email')}
                    >
                      <span className="break-all">{employee.email||'N/A'}</span>
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-900 rounded-2xl border border-gray-800/50 p-8 max-md:p-4 shadow-2xl">
                <h2 className="text-xl font-bold mb-6">Home Address</h2>
                <p className="text-lg space-y-1">
                  <span
                    className="cursor-pointer relative group inline-block"
                    onClick={() => copyToClipboard(employee.homeAddress?.street||'N/A','street')}
                  >
                    {employee.homeAddress?.street||'N/A'}
                    {copiedCell?.field==='street'&&(
                      <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-zinc-200 text-xs px-3 py-1 rounded-md shadow-lg z-10">
                        Copied!
                      </span>
                    )}
                  </span><br />
                  <span
                    className="cursor-pointer relative group inline-block"
                    onClick={() => copyToClipboard(
                      `${employee.homeAddress?.city||'N/A'}, ${employee.homeAddress?.state||'N/A'} ${employee.homeAddress?.zip||'N/A'}`,
                      'address'
                    )}
                  >
                    {employee.homeAddress?.city||'N/A'}, {employee.homeAddress?.state||'N/A'} {employee.homeAddress?.zip||'N/A'}
                    {copiedCell?.field==='address'&&(
                      <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-zinc-200 text-xs px-3 py-1 rounded-md shadow-lg z-10">
                        Copied!
                      </span>
                    )}
                  </span>
                </p>
              </div>

              <div className="bg-gray-900 rounded-2xl border border-gray-800/50 p-8 max-md:p-4 shadow-2xl">
                <h2 className="text-xl font-bold mb-6">Employment Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
                  <div>
                    <p className="text-zinc-400 text-sm">Employee #</p>
                    <p className="text-lg font-medium mt-1">{employee.employeeNumber||'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">Start Date</p>
                    <p className="text-lg font-medium mt-1">
                      {startDate ? startDate.toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">Pay Rate</p>
                    <p className="text-lg font-medium mt-1">
                      {employee.payRate ? `$${Number(employee.payRate).toFixed(2)}/hr` : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">Last Raise Date</p>
                    <p className="text-lg font-medium mt-1">
                      {lastRaiseDate ? lastRaiseDate.toLocaleDateString() : 'None yet'}
                    </p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">Last Raise Amount</p>
                    <p className="text-lg font-medium mt-1 text-green-400">
                      {employee.lastRaiseAmount ? `+$${Number(employee.lastRaiseAmount).toFixed(2)}` : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">Hours This Week</p>
                    <p className="text-lg font-medium mt-1">{employee.hoursThisWeek?.toFixed(2)||'0.00'} hrs</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">Total Hours</p>
                    <p className="text-lg font-medium mt-1">{employee.totalHours?.toLocaleString()||'0'}</p>
                  </div>
                </div>

                <h3 className="text-lg font-semibold mb-2 text-zinc-200">Pay Rate History</h3>
                <div className="h-64 bg-gray-950/50 rounded-xl p-4 border border-gray-800 mb-3">
                  <Line data={chartData} options={chartOptions} />
                </div>

                <div className="flex justify-end gap-4 text-xs text-zinc-500 mt-2">
                  <button
                    onClick={() => setTimeRange('all')}
                    className={`hover:text-zinc-300 transition-colors ${timeRange==='all'?'text-zinc-200 underline':''}`}
                  >
                    All Time
                  </button>
                  <span className="text-zinc-600">|</span>
                  <button
                    onClick={() => setTimeRange('12m')}
                    className={`hover:text-zinc-300 transition-colors ${timeRange==='12m'?'text-zinc-200 underline':''}`}
                  >
                    12 Months
                  </button>
                  <span className="text-zinc-600">|</span>
                  <button
                    onClick={() => setTimeRange('6m')}
                    className={`hover:text-zinc-300 transition-colors ${timeRange==='6m'?'text-zinc-200 underline':''}`}
                  >
                    6 Months
                  </button>
                  <span className="text-zinc-600">|</span>
                  <button
                    onClick={() => setTimeRange('30d')}
                    className={`hover:text-zinc-300 transition-colors ${timeRange==='30d'?'text-zinc-200 underline':''}`}
                  >
                    30 Days
                  </button>
                </div>
              </div>
            </div>

            <div className="space-y-8">
              <div className="bg-gray-900 rounded-2xl border border-gray-800/50 p-6 shadow-2xl">
                <h2 className="text-xl font-bold mb-6">Certifications</h2>
                <ul className="space-y-3 text-zinc-300">
                  {(employee.certifications||[]).map((c,i)=>(
                    <li key={i} className="flex items-start gap-3">
                      <span className="text-green-500 mt-1">✓</span>{c}
                    </li>
                  ))}
                  {(!employee.certifications?.length)&&<li className="text-zinc-500">No certifications listed</li>}
                </ul>
              </div>

              <div className="bg-gray-900 rounded-2xl border border-gray-800/50 p-6 shadow-2xl">
                <h2 className="text-xl font-bold mb-6">Education / Training</h2>
                <p className="text-zinc-300">{employee.education||'Not listed'}</p>
              </div>
            </div>
          </div>
        )}

        {/* Other tabs */}
        {activeTab === 'projects' && (
          <div className="bg-gray-900 rounded-2xl border border-gray-800/50 p-8 max-md:p-4 shadow-2xl">
            <h2 className="text-xl font-bold mb-6">Project History</h2>
            <p className="text-zinc-500">Project history not tracked yet.</p>
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="bg-gray-900 rounded-2xl border border-gray-800/50 p-8 max-md:p-4 shadow-2xl">
            <h2 className="text-xl font-bold mb-6">Documents & Certifications</h2>
            <ul className="space-y-4">
              {(employee.documents||[]).map((d,i)=>(
                <li key={i} className="flex items-center gap-4">
                  <span className="text-indigo-400 text-xl">📄</span><span>{d}</span>
                </li>
              ))}
            </ul>
            <button className="mt-8 text-indigo-400 hover:text-indigo-300 text-sm">
              + Upload New Document / Certificate
            </button>
          </div>
        )}

        {activeTab === 'notes' && (
          <div className="bg-gray-900 rounded-2xl border border-gray-800/50 p-8 max-md:p-4 shadow-2xl">
            <h2 className="text-xl font-bold mb-6">Notes & Observations</h2>
            <p className="text-zinc-300 whitespace-pre-line leading-relaxed">
              {employee.notes||'No notes available.'}
            </p>
            <button className="mt-8 text-indigo-400 hover:text-indigo-300 text-sm">
              + Add Note
            </button>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-2xl p-8 w-full max-w-3xl max-h-[90vh] overflow-y-auto border border-gray-700">
            <h2 className="text-2xl font-bold mb-6">Edit Employee Profile</h2>

            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={formatPhone(formData.phone)}
                    onChange={handlePhoneChange}
                    placeholder="(123) 456-7890"
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-xl text-white"
                    maxLength={14}
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Email</label>
                  <input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-xl text-white"
                  />
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4">Home Address</h3>

              {/* Google Places search (same UX as adding a project), with a
                  manual-entry escape hatch below. */}
              {!manualAddr && (
                <div className="mb-4 relative">
                  <input
                    value={addrQuery}
                    onChange={(e) => { setAddrQuery(e.target.value); fetchAddrSuggestions(e.target.value); }}
                    placeholder="Type to search address..."
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-xl text-white focus:border-indigo-500 outline-none"
                  />
                  {addrError && <p className="text-xs text-amber-400 mt-1">{addrError}</p>}
                  {addrSuggestions.length > 0 && (
                    <div className="absolute z-30 mt-1 w-full bg-gray-800 border border-gray-700 rounded-xl overflow-hidden shadow-2xl max-h-52 overflow-y-auto">
                      {addrSuggestions.map(s => (
                        <button
                          key={s.placeId || s.description}
                          type="button"
                          onClick={async () => {
                            setAddrSuggestions([]);
                            setAddrQuery(s.description);
                            try {
                              const parts = await geocodePlaceId(s.placeId);
                              setFormData(prev => ({ ...prev, homeAddress: { ...prev.homeAddress, ...parts } }));
                            } catch (_) {
                              setAddrError('Could not read that address — enter it manually below.');
                              setManualAddr(true);
                            }
                          }}
                          className="w-full text-left px-4 py-2.5 text-sm text-zinc-200 hover:bg-indigo-600/30 border-b border-gray-700/50 last:border-0"
                        >
                          📍 {s.description}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <button
                type="button"
                onClick={() => { setManualAddr(v => !v); setAddrSuggestions([]); }}
                className="mb-4 text-sm text-indigo-400 hover:text-indigo-300 hover:underline underline-offset-2"
              >
                {manualAddr ? '← Back to address search' : 'Enter address manually'}
              </button>

              {manualAddr && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm text-zinc-400 mb-1">Street</label>
                  <input
                    name="homeAddress.street"
                    value={formData.homeAddress.street}
                    onChange={handleFormChange}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-xl text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">City</label>
                  <input
                    name="homeAddress.city"
                    value={formData.homeAddress.city}
                    onChange={handleFormChange}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-xl text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">State</label>
                  <input
                    name="homeAddress.state"
                    value={formData.homeAddress.state}
                    onChange={handleFormChange}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-xl text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Zip</label>
                  <input
                    name="homeAddress.zip"
                    value={formData.homeAddress.zip}
                    onChange={handleFormChange}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-xl text-white"
                  />
                </div>
              </div>
              )}
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4">Employment Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Start Date</label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleFormChange}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-xl text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Current Pay Rate ($/hr)</label>
                  <input
                    type="number"
                    step="0.01"
                    name="payRate"
                    value={formData.payRate}
                    onChange={handleFormChange}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-xl text-white"
                  />
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4">Pay Raise History</h3>
              <div className="mb-6 overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-800">
                      <th className="p-3">Raise Date</th>
                      <th className="p-3">Raise Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(employee.payRaiseHistory||[]).length ? (
                      employee.payRaiseHistory.map((r,i)=>(
                        <tr key={i} className="border-t border-gray-700">
                          <td className="p-3">{r.date ? new Date(r.date).toLocaleDateString() : '—'}</td>
                          <td className="p-3">${r.raiseAmount?.toFixed(2)||'0.00'}</td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan={2} className="p-3 text-center text-zinc-500">No raises recorded</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              <h4 className="text-md font-medium mb-3">Add New Raise(s)</h4>
              {formData.newRaises.map((r,index)=>(
                <div key={index} className="flex flex-wrap gap-4 mb-4 p-4 bg-gray-800 rounded-xl">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Date</label>
                    <input
                      type="date"
                      value={r.date}
                      onChange={e=>updateNewRaise(index,'date',e.target.value)}
                      className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Pay Rate</label>
                    <input
                      type="number"
                      step="0.01"
                      value={r.payRate}
                      onChange={e=>updateNewRaise(index,'payRate',e.target.value)}
                      className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white w-32"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Raise Amt</label>
                    <input
                      type="number"
                      step="0.01"
                      value={r.raiseAmount}
                      onChange={e=>updateNewRaise(index,'raiseAmount',e.target.value)}
                      className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white w-32"
                    />
                  </div>
                  <button
                    onClick={()=>removeNewRaise(index)}
                    className="px-3 py-2 bg-red-900 hover:bg-red-800 text-white rounded-lg self-end"
                  >
                    Remove
                  </button>
                </div>
              ))}

              <button
                onClick={addRaiseRow}
                className="px-5 py-2 bg-indigo-700 hover:bg-indigo-600 rounded-xl text-white flex items-center gap-2"
              >
                + Add Raise
              </button>
            </div>

            <div className="flex justify-end gap-4 mt-8">
              <button
                onClick={()=>setIsEditModalOpen(false)}
                className="px-6 py-3 bg-gray-700 hover:bg-gray-600 rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-6 py-3 bg-indigo-700 hover:bg-indigo-600 rounded-xl shadow-md"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmployeeProfile;